import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Camera, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { LOGO_URL } from "@/i18n/translations";
import { spring } from "./bits";

// Full-screen photo/video publication consent — shown on EVERY booking completion
// in the staff app. The customer answers on the device; both choices complete the booking.
const ConsentScreen = ({ booking, onDone }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [busy, setBusy] = useState(false);

  const pick = async (choice) => {
    if (busy) return;
    setBusy(true);
    try {
      await appApi.post(`/app/bookings/${booking.type}/${booking.id}/complete-consent`, { consent: choice });
      toast.success(m.status_done_toast);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || m.error_generic);
      setBusy(false);
    }
  };

  const petLine = booking.type === "grooming"
    ? [booking.pet_name, booking.pet_breed].filter(Boolean).join(" · ")
    : [booking.plan, booking.nagayu ? "Nagayu" : null].filter(Boolean).join(" · ");

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-brand-cream flex flex-col overflow-hidden"
      data-testid="consent-screen"
    >
      <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 bg-brand-soft/60 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-64 w-64 bg-brand/5 rounded-full blur-3xl" />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10 max-w-md mx-auto w-full">
        <motion.img
          src={LOGO_URL}
          alt="Care For Me"
          className="h-12 object-contain"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        />

        <motion.div
          initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ delay: 0.12, ...spring }}
          className="mt-8 grid place-items-center h-20 w-20 rounded-[1.6rem] bg-white shadow-[0_16px_44px_rgba(134,77,99,0.22)]"
        >
          <Camera className="h-9 w-9 text-brand" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
          className="mt-6 font-heading text-2xl font-medium text-brand-ink text-center"
          data-testid="consent-title"
        >
          {m.consent_title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-4 text-center text-base leading-relaxed text-brand-ink/85 font-medium"
          data-testid="consent-message"
        >
          {m.consent_msg}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="mt-5 rounded-full bg-white border border-brand/15 px-5 py-2 text-sm font-bold text-brand-ink/70 shadow-sm"
          data-testid="consent-customer"
        >
          {booking.customer_name}{petLine ? ` · ${petLine}` : ""}
        </motion.div>

        <div className="w-full mt-9 space-y-4">
          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, ...spring }}
            whileTap={{ scale: 0.96 }}
            onClick={() => pick("granted")}
            disabled={busy}
            className="w-full h-20 rounded-3xl bg-emerald-500 text-white text-xl font-black tracking-wide flex items-center justify-center gap-3 shadow-[0_14px_36px_rgba(16,185,129,0.4)] disabled:opacity-50"
            data-testid="consent-yes"
          >
            <Check className="h-7 w-7" strokeWidth={3} />
            {m.consent_yes}
          </motion.button>
          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, ...spring }}
            whileTap={{ scale: 0.96 }}
            onClick={() => pick("declined")}
            disabled={busy}
            className="w-full h-20 rounded-3xl bg-rose-500 text-white text-xl font-black tracking-wide flex items-center justify-center gap-3 shadow-[0_14px_36px_rgba(244,63,94,0.35)] disabled:opacity-50"
            data-testid="consent-no"
          >
            <X className="h-7 w-7" strokeWidth={3} />
            {m.consent_no}
          </motion.button>
        </div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
          className="mt-6 text-center text-xs text-brand-ink/45"
          data-testid="consent-hint"
        >
          {m.consent_hint}
        </motion.p>
      </div>
    </motion.div>,
    document.body
  );
};

export default ConsentScreen;
